import React, { useState, useEffect, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context/AuthContext";
import { admins, verifiedContentUsers } from "../Constants";
import Loading from "../components/Loading";
import { roleDisplayNames } from "../models/PlayerRole";
import { PersonSearch, Assessment, TrendingUp, EmojiEvents, LockOutlined, MapOutlined } from "@mui/icons-material";
import {
    ScoutResponse,
    ScoutPlayerResult,
    ScoutMapArea,
    ScoutFilters,
    scoutByRole,
    scoutByArea,
    getScoutFilters,
    getScoutMaps,
    getAreasByMap,
    scoutMyDemosRole,
    scoutMyDemosArea,
    getMyDemosScoutMaps,
    getMyDemosScoutAreas,
} from "../services/PlayerScoutService";

const tSideRoles = ["lurk", "half_lurk", "pack", "t_allround", "awp"];
const ctSideRoles = ["anchor", "rotation", "ct_allround", "awp"];

const divisionDisplayNames: Record<string, string> = {
    ligaen: "Ligaen",
    "1div": "1. Division",
    "2div": "2. Division",
    "3div": "3. Division",
    "4div": "4. Division",
    "5div": "5. Division",
};

const dropdownBtnStyle: React.CSSProperties = {
    background: "rgba(255, 255, 255, 0.05)",
    color: "rgba(255, 255, 255, 0.9)",
    border: "1px solid rgba(255, 102, 0, 0.2)",
    borderRadius: "6px",
    padding: "8px 16px",
    fontWeight: "500",
    transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease",
};

const dropdownMenuStyle: React.CSSProperties = {
    background: "rgba(26, 26, 26, 0.95)",
    border: "1px solid rgba(255, 102, 0, 0.2)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
    backdropFilter: "blur(10px)",
    maxHeight: "300px",
    overflowY: "auto",
};

const dropdownItemStyle: React.CSSProperties = {
    color: "rgba(255, 255, 255, 0.8)",
    transition: "background-color 0.2s ease, color 0.2s ease",
};

const salesCardStyle: React.CSSProperties = {
    background: "#2d2d2d",
    borderRadius: "12px",
    border: "1px solid rgba(255, 102, 0, 0.2)",
    padding: "28px 24px",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    height: "100%",
    textAlign: "left",
};

const salesCtaStyle: React.CSSProperties = {
    backgroundColor: "#f15c25",
    color: "white",
    fontWeight: 600,
    padding: "14px 40px",
    borderRadius: "6px",
    border: "none",
    fontSize: "1.1rem",
    transition: "background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
    textDecoration: "none",
    display: "inline-block",
};

const salesMetricStyle: React.CSSProperties = {
    background: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 102, 0, 0.15)",
    borderRadius: "8px",
    padding: "14px 18px",
    textAlign: "center",
    flex: "1",
    minWidth: "100px",
};

const DropdownItem: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
    <li>
        <button
            className="dropdown-item"
            onClick={onClick}
            style={dropdownItemStyle}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 102, 0, 0.1)";
                e.currentTarget.style.color = "#ff9933";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "rgba(255, 255, 255, 0.8)";
            }}
        >
            {label}
        </button>
    </li>
);

const PlayerScoutPage: React.FC = () => {
    const { user, authLoaded } = useContext(AuthContext);
    const { t } = useTranslation("pages");

    // Data source: league-wide vs the donator's own uploaded demos (pooled)
    const [dataSource, setDataSource] = useState<"league" | "demos">("league");

    // Search mode
    const [searchMode, setSearchMode] = useState<"role" | "area">("role");

    // Shared filters
    const [season, setSeason] = useState<number>(0);
    const [side, setSide] = useState<string | null>(null);
    const [division, setDivision] = useState<string | null>(null);

    // Role filters
    const [roleCategory, setRoleCategory] = useState<string>("lurk");

    // Role threshold filters
    const [minRounds, setMinRounds] = useState<number>(10);
    const [minRolePercentage, setMinRolePercentage] = useState<number>(0);

    // Sorting
    const [sortColumn, setSortColumn] = useState<string>("rating");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

    // Area filters
    const [selectedMap, setSelectedMap] = useState<string | null>(null);
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
    const [areas, setAreas] = useState<ScoutMapArea[]>([]);

    // Available filter options
    const [filters, setFilters] = useState<ScoutFilters | null>(null);
    const [maps, setMaps] = useState<string[]>([]);
    const [divisions] = useState<string[]>([
        "ligaen", "1div", "2div", "3div", "4div", "5div",
    ]);

    // Results
    const [results, setResults] = useState<ScoutResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [filtersLoading, setFiltersLoading] = useState(true);
    const [error, setError] = useState<string>("");

    // Load initial filters
    useEffect(() => {
        const loadFilters = async () => {
            try {
                const f = await getScoutFilters();
                setFilters(f);
                if (f.seasons.length > 0) {
                    setSeason(f.seasons[0]);
                }
                setMaps(f.maps);
            } catch (err) {
                console.error("Error loading scout filters:", err);
            } finally {
                setFiltersLoading(false);
            }
        };
        loadFilters();
    }, []);

    // Update maps when season or data source changes
    useEffect(() => {
        const loadMaps = async () => {
            try {
                if (dataSource === "demos") {
                    setMaps(await getMyDemosScoutMaps());
                } else {
                    if (season === 0) return;
                    setMaps(await getScoutMaps(season));
                }
            } catch (err) {
                console.error("Error loading maps:", err);
            }
        };
        loadMaps();
    }, [season, dataSource]);

    // Reset selections when switching data source so stale league/demo maps don't carry over
    useEffect(() => {
        setSelectedMap(null);
        setSelectedAreaId(null);
        setResults(null);
    }, [dataSource]);

    // Load areas when map (or data source) changes
    useEffect(() => {
        if (!selectedMap) {
            setAreas([]);
            setSelectedAreaId(null);
            return;
        }
        const loadAreas = async () => {
            try {
                const a = dataSource === "demos"
                    ? await getMyDemosScoutAreas(selectedMap)
                    : await getAreasByMap(selectedMap);
                setAreas(a);
                setSelectedAreaId(null);
            } catch (err) {
                console.error("Error loading areas:", err);
            }
        };
        loadAreas();
    }, [selectedMap, dataSource]);

    // Get available roles based on side filter
    const availableRoles = React.useMemo(() => {
        if (!filters) return [];
        if (side === "T") return tSideRoles;
        if (side === "CT") return ctSideRoles;
        return filters.roleCategories;
    }, [filters, side]);

    // Reset role selection when side changes and current role is invalid
    useEffect(() => {
        if (availableRoles.length > 0 && !availableRoles.includes(roleCategory)) {
            setRoleCategory(availableRoles[0]);
        }
    }, [availableRoles, roleCategory]);

    const handleSearch = useCallback(async () => {
        if (dataSource === "league" && season === 0) return;

        setLoading(true);
        setError("");
        setResults(null);

        // Wrap a bare player array (from the owner-scoped endpoints) into a ScoutResponse shape.
        const wrap = (players: ScoutPlayerResult[]): ScoutResponse => ({
            searchType: searchMode, roleCategory: searchMode === "role" ? roleCategory : null,
            mapName: selectedMap, areaName: null, side, season: 0, division: null, minThreshold: 0, players,
        });

        try {
            const isAwp = roleCategory === "awp";
            if (searchMode === "role") {
                if (dataSource === "demos") {
                    setResults(wrap(await scoutMyDemosRole(roleCategory, side, selectedMap,
                        isAwp ? 0 : minRounds, isAwp ? 0 : minRolePercentage)));
                } else {
                    setResults(await scoutByRole(roleCategory, season, side, division, selectedMap,
                        isAwp ? 0 : minRounds, 50, isAwp ? 0 : minRolePercentage));
                }
            } else {
                if (!selectedAreaId) {
                    setError("Please select a map and area.");
                    setLoading(false);
                    return;
                }
                if (dataSource === "demos") {
                    setResults(wrap(await scoutMyDemosArea(selectedAreaId, side)));
                } else {
                    setResults(await scoutByArea(selectedAreaId, season, side, division));
                }
            }
        } catch (err) {
            setError("Error searching. Please try again.");
            console.error("Scout error:", err);
        } finally {
            setLoading(false);
        }
    }, [dataSource, searchMode, roleCategory, season, side, division, selectedMap, selectedAreaId, minRounds, minRolePercentage]);

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("desc");
        }
    };

    const sortedPlayers = React.useMemo(() => {
        if (!results) return [];
        const players = [...results.players];
        const getValue = (p: ScoutPlayerResult, col: string): number => {
            switch (col) {
                case "roleRounds": return p.roleRounds;
                case "rolePercentage": return p.rolePercentage;
                case "games": return p.gamesCount;
                case "rating": return p.avgRating21;
                case "adr": return p.avgAdr;
                case "kd": return p.avgKd;
                case "kast": return p.avgKast;
                case "time": return p.totalTimeSeconds;
                case "areaPercentage": return p.areaTimePercentage;
                default: return 0;
            }
        };
        players.sort((a, b) => {
            const diff = getValue(a, sortColumn) - getValue(b, sortColumn);
            return sortDirection === "asc" ? diff : -diff;
        });
        return players;
    }, [results, sortColumn, sortDirection]);

    const isAdmin = user && admins.includes(user.steamID);
    const isContentCreator = user && verifiedContentUsers.includes(user.steamID);

    // Wait for auth to fully load (including donator status from API)
    if (!authLoaded) {
        return <div className="text-center" style={{ paddingTop: "60px" }}><Loading /></div>;
    }

    // Donator/team-tier/admin/content creator gate — sales landing page
    if (!user?.donator && !user?.teamTier && !isAdmin && !isContentCreator) {
        const cardHover = (e: React.MouseEvent<HTMLDivElement>) => {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 8px 24px rgba(255, 102, 0, 0.15)";
        };
        const cardLeave = (e: React.MouseEvent<HTMLDivElement>) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
        };
        const ctaHover = (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.currentTarget.style.backgroundColor = "#e25623";
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 4px 16px rgba(241, 92, 37, 0.4)";
        };
        const ctaLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.currentTarget.style.backgroundColor = "#f15c25";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
        };

        const fakeRows = [
            { rank: 1, name: "player_a", team: "Team Alpha", div: "Ligaen", rounds: 142, pct: "67%", games: 14, rating: 1.25, adr: 87.3, kd: 1.32, kast: "76.2%" },
            { rank: 2, name: "player_b", team: "Team Beta", div: "1. Division", rounds: 118, pct: "58%", games: 12, rating: 1.18, adr: 82.1, kd: 1.21, kast: "74.8%" },
            { rank: 3, name: "player_c", team: "Team Gamma", div: "Ligaen", rounds: 105, pct: "52%", games: 11, rating: 1.12, adr: 79.5, kd: 1.15, kast: "71.3%" },
            { rank: 4, name: "player_d", team: "Team Delta", div: "2. Division", rounds: 96, pct: "48%", games: 10, rating: 1.08, adr: 76.8, kd: 1.08, kast: "69.5%" },
        ];

        return (
            <div className="pb-3 text-white">
                <div className="container" style={{ maxWidth: "960px" }}>

                    {/* Section 1: Hero */}
                    <div style={{ textAlign: "center", padding: "60px 0 40px" }}>
                        <div style={{ width: "60px", height: "4px", background: "#f15c25", margin: "0 auto 24px", borderRadius: "2px" }} />
                        <h1 style={{ color: "#e2e8f0", fontWeight: 700, fontSize: "2.5rem", marginBottom: "20px" }}>
                            {t("scout.title")}
                        </h1>
                        <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "1.1rem", maxWidth: "620px", margin: "0 auto 32px", lineHeight: 1.7 }}>
                            {t("scout.heroSubtitle")}
                        </p>
                        <Link to="/donations" style={salesCtaStyle} onMouseEnter={ctaHover} onMouseLeave={ctaLeave}>
                            {t("scout.unlockCta")}
                        </Link>
                    </div>

                    {/* Section 2: Search Mode Cards */}
                    <div style={{ marginBottom: "48px" }}>
                        <div className="row g-4">
                            <div className="col-md-6">
                                <div style={salesCardStyle} onMouseEnter={cardHover} onMouseLeave={cardLeave}>
                                    <PersonSearch style={{ fontSize: 48, color: "#f15c25", marginBottom: "16px" }} />
                                    <h4 style={{ color: "#e2e8f0", fontWeight: 600, marginBottom: "12px" }}>{t("scout.searchByRoleTitle")}</h4>
                                    <p style={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.6, marginBottom: "16px" }}>
                                        {t("scout.searchByRoleDesc")}
                                    </p>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                        {["Lurker", "Anchor", "AWPer", "Pack", "Rotation"].map(role => (
                                            <span key={role} style={{
                                                background: "rgba(255,102,0,0.1)",
                                                border: "1px solid rgba(255,102,0,0.25)",
                                                borderRadius: "4px",
                                                padding: "3px 10px",
                                                fontSize: "12px",
                                                color: "#ff9933",
                                            }}>
                                                {role}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div style={salesCardStyle} onMouseEnter={cardHover} onMouseLeave={cardLeave}>
                                    <MapOutlined style={{ fontSize: 48, color: "#f15c25", marginBottom: "16px" }} />
                                    <h4 style={{ color: "#e2e8f0", fontWeight: 600, marginBottom: "12px" }}>{t("scout.searchByPositionTitle")}</h4>
                                    <p style={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.6, marginBottom: "16px" }}>
                                        {t("scout.searchByPositionDesc")}
                                    </p>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                        {["B Apps", "A Site", "Mid", "Connector", "Banana"].map(area => (
                                            <span key={area} style={{
                                                background: "rgba(255,102,0,0.1)",
                                                border: "1px solid rgba(255,102,0,0.25)",
                                                borderRadius: "4px",
                                                padding: "3px 10px",
                                                fontSize: "12px",
                                                color: "#ff9933",
                                            }}>
                                                {area}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Stats Metrics Bar */}
                    <div style={{ marginBottom: "48px" }}>
                        <h3 style={{ textAlign: "center", color: "#e2e8f0", fontWeight: 600, marginBottom: "8px" }}>
                            {t("scout.statsTitle")}
                        </h3>
                        <p style={{ textAlign: "center", color: "rgba(255,255,255,0.5)", marginBottom: "24px" }}>
                            {t("scout.statsSubtitle")}
                        </p>
                        <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                            {[
                                { label: t("scout.metricRating"), value: "1.25", color: "#4ade80" },
                                { label: t("scout.metricAdr"), value: "87.3", color: "#e2e8f0" },
                                { label: t("scout.metricKd"), value: "1.32", color: "#e2e8f0" },
                                { label: t("scout.metricKast"), value: "74.1%", color: "#e2e8f0" },
                                { label: t("scout.metricGames"), value: "12", color: "#e2e8f0" },
                            ].map(metric => (
                                <div key={metric.label} style={salesMetricStyle}>
                                    <div style={{ fontSize: "1.3rem", fontWeight: 700, color: metric.color }}>{metric.value}</div>
                                    <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.5)", marginTop: "4px" }}>{metric.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Section 4: Use Case Cards */}
                    <div style={{ marginBottom: "48px" }}>
                        <div className="row g-4">
                            {[
                                { icon: <Assessment style={{ fontSize: 40, color: "#f15c25", marginBottom: "12px" }} />, title: t("scout.useCaseManagerTitle"), desc: t("scout.useCaseManagerDesc") },
                                { icon: <TrendingUp style={{ fontSize: 40, color: "#f15c25", marginBottom: "12px" }} />, title: t("scout.useCaseCoachTitle"), desc: t("scout.useCaseCoachDesc") },
                                { icon: <EmojiEvents style={{ fontSize: 40, color: "#f15c25", marginBottom: "12px" }} />, title: t("scout.useCasePlayerTitle"), desc: t("scout.useCasePlayerDesc") },
                            ].map((card, i) => (
                                <div key={i} className="col-md-4">
                                    <div style={{ ...salesCardStyle, textAlign: "center" }} onMouseEnter={cardHover} onMouseLeave={cardLeave}>
                                        {card.icon}
                                        <h5 style={{ color: "#e2e8f0", fontWeight: 600, marginBottom: "8px" }}>{card.title}</h5>
                                        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.95rem", lineHeight: 1.5, margin: 0 }}>{card.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Section 5: Blurred Preview Table */}
                    <div style={{ marginBottom: "48px" }}>
                        <h3 style={{ textAlign: "center", color: "#e2e8f0", fontWeight: 600, marginBottom: "24px" }}>
                            {t("scout.previewTitle")}
                        </h3>
                        <div style={{ position: "relative", borderRadius: "12px", overflow: "hidden" }}>
                            {/* Fake table */}
                            <div style={{
                                filter: "blur(3px)",
                                pointerEvents: "none",
                                userSelect: "none",
                                maxHeight: "280px",
                                overflow: "hidden",
                                background: "linear-gradient(135deg, #1a1a1a 0%, #252525 100%)",
                                border: "1px solid rgba(255, 102, 0, 0.1)",
                                borderRadius: "12px",
                            }}>
                                <div style={{
                                    background: "linear-gradient(135deg, #2a2a2a 0%, #333333 100%)",
                                    padding: "12px 16px",
                                    borderBottom: "2px solid rgba(255, 102, 0, 0.15)",
                                }}>
                                    <h5 style={{ margin: 0, color: "#e2e8f0", fontSize: "1.05rem" }}>
                                        Best Lurkers — Season 31
                                    </h5>
                                </div>
                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr style={{ background: "linear-gradient(135deg, #2a2a2a 0%, #333333 100%)" }}>
                                                {["#", "Player", "Team", "Division", "Role Rounds", "Role %", "Games", "Rating", "ADR", "K/D", "KAST"].map(h => (
                                                    <th key={h} style={{
                                                        color: "#e2e8f0", padding: "8px 10px", fontWeight: 600,
                                                        fontSize: "13px", textAlign: ["Player", "Team", "Division"].includes(h) ? "left" : "center",
                                                        whiteSpace: "nowrap",
                                                    }}>
                                                        {h}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fakeRows.map((row, i) => (
                                                <tr key={i} style={{
                                                    backgroundColor: i % 2 === 0 ? "rgba(42,42,42,0.5)" : "rgba(36,36,36,0.5)",
                                                    color: "#e2e8f0",
                                                }}>
                                                    <td style={{ textAlign: "center", padding: "8px", fontWeight: "bold", color: "#e25623", width: "40px" }}>{row.rank}</td>
                                                    <td style={{ padding: "8px 10px" }}>{row.name}</td>
                                                    <td style={{ padding: "8px 10px", color: "rgba(255,255,255,0.7)", fontSize: "13px" }}>{row.team}</td>
                                                    <td style={{ padding: "8px 10px", color: "rgba(255,255,255,0.6)", fontSize: "13px" }}>{row.div}</td>
                                                    <td style={{ textAlign: "center", padding: "8px", fontSize: "13px" }}>{row.rounds}</td>
                                                    <td style={{ textAlign: "center", padding: "8px", fontSize: "13px", color: "rgba(255,255,255,0.6)" }}>{row.pct}</td>
                                                    <td style={{ textAlign: "center", padding: "8px", fontSize: "13px" }}>{row.games}</td>
                                                    <td style={{ textAlign: "center", padding: "8px", fontWeight: "bold", color: ratingColor(row.rating) }}>{row.rating.toFixed(2)}</td>
                                                    <td style={{ textAlign: "center", padding: "8px", fontSize: "13px" }}>{row.adr.toFixed(1)}</td>
                                                    <td style={{ textAlign: "center", padding: "8px", fontSize: "13px" }}>{row.kd.toFixed(2)}</td>
                                                    <td style={{ textAlign: "center", padding: "8px", fontSize: "13px" }}>{row.kast}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            {/* Gradient overlay */}
                            <div style={{
                                position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                                background: "linear-gradient(to bottom, transparent 0%, rgba(26,26,26,0.7) 50%, rgba(26,26,26,0.98) 100%)",
                                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end",
                                paddingBottom: "32px", borderRadius: "12px",
                            }}>
                                <LockOutlined style={{ fontSize: 36, color: "rgba(255,255,255,0.4)", marginBottom: "12px" }} />
                                <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.9rem", margin: 0 }}>
                                    {t("scout.previewLocked")}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Section 6: CTA Footer */}
                    <div style={{ textAlign: "center", padding: "20px 0 60px" }}>
                        <h3 style={{ color: "#e2e8f0", fontWeight: 600, marginBottom: "16px" }}>
                            {t("scout.ctaTitle")}
                        </h3>
                        <p style={{ color: "rgba(255,255,255,0.7)", maxWidth: "500px", margin: "0 auto 24px", lineHeight: 1.6 }}>
                            {t("scout.ctaDescription")}
                        </p>
                        <Link to="/donations" style={salesCtaStyle} onMouseEnter={ctaHover} onMouseLeave={ctaLeave}>
                            {t("scout.ctaButton")}
                        </Link>
                        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginTop: "16px" }}>
                            {t("scout.ctaNote")}
                        </p>
                    </div>

                </div>
            </div>
        );
    }

    if (filtersLoading) return <Loading />;

    const selectedAreaName = areas.find((a) => a.id === selectedAreaId)?.areaName;

    const sideButton = (label: string, value: string | null) => (
        <button
            className="btn"
            onClick={() => setSide(value)}
            style={{
                minWidth: "100px",
                fontWeight: side === value ? "bold" : "normal",
                backgroundColor: side === value ? "#e25623" : "transparent",
                color: side === value ? "white" : "#e2e8f0",
                border: side === value ? "none" : "1px solid #444",
            }}
        >
            {label}
        </button>
    );

    const modeButton = (label: string, mode: "role" | "area") => (
        <button
            className="btn"
            onClick={() => {
                setSearchMode(mode);
                setResults(null);
            }}
            style={{
                minWidth: "130px",
                fontWeight: searchMode === mode ? "bold" : "normal",
                backgroundColor: searchMode === mode ? "#e25623" : "transparent",
                color: searchMode === mode ? "white" : "#e2e8f0",
                border: searchMode === mode ? "none" : "1px solid #444",
            }}
        >
            {label}
        </button>
    );

    const sourceButton = (label: string, value: "league" | "demos") => (
        <button
            className="btn"
            onClick={() => setDataSource(value)}
            style={{
                minWidth: "140px",
                fontWeight: dataSource === value ? "bold" : "normal",
                backgroundColor: dataSource === value ? "#e25623" : "transparent",
                color: dataSource === value ? "white" : "#e2e8f0",
                border: dataSource === value ? "none" : "1px solid #444",
            }}
        >
            {label}
        </button>
    );

    return (
        <div className="pb-3 text-white">
            <div className="container text-center">
                <h1 style={{ color: "#e2e8f0" }}>Player Scout</h1>
                <p style={{ color: "rgba(255,255,255,0.7)" }}>
                    Find the best-rated players by role or position.
                </p>
                <hr />

                {/* Data source: league vs your own uploaded demos */}
                <div className="d-flex justify-content-center mb-2 gap-2">
                    {sourceButton("League", "league")}
                    {sourceButton("My demos", "demos")}
                </div>
                {dataSource === "demos" && (
                    <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", marginBottom: "16px" }}>
                        Pooled across all the demos you've uploaded — only you can see this.
                    </p>
                )}

                {/* Mode Toggle */}
                <div className="d-flex justify-content-center mb-3 gap-2">
                    {modeButton("By Role", "role")}
                    {modeButton("By Position", "area")}
                </div>

                {/* Side Toggle */}
                <div className="d-flex justify-content-center mb-3 gap-2">
                    {sideButton("Overall", null)}
                    {sideButton("T", "T")}
                    {sideButton("CT", "CT")}
                </div>

                {/* Filter Dropdowns */}
                <div className="row justify-content-center text-center mb-3 g-2">
                    {/* Season + Division — league mode only (uploads have no season/division) */}
                    {dataSource === "league" && (<>
                    <div className="col-6 col-md-2">
                        <span style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "13px" }}>Season</span>
                        <div className="dropdown">
                            <button
                                className="btn dropdown-toggle w-100"
                                type="button"
                                data-bs-toggle="dropdown"
                                style={dropdownBtnStyle}
                            >
                                {season > 0 ? `Season ${season}` : "Select"}
                            </button>
                            <ul className="dropdown-menu w-100" style={dropdownMenuStyle}>
                                {filters?.seasons.map((s) => (
                                    <DropdownItem key={s} label={`Season ${s}`} onClick={() => setSeason(s)} />
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Division */}
                    <div className="col-6 col-md-2">
                        <span style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "13px" }}>Division</span>
                        <div className="dropdown">
                            <button
                                className="btn dropdown-toggle w-100"
                                type="button"
                                data-bs-toggle="dropdown"
                                style={dropdownBtnStyle}
                            >
                                {division ? divisionDisplayNames[division] || division : "All"}
                            </button>
                            <ul className="dropdown-menu w-100" style={dropdownMenuStyle}>
                                <DropdownItem label="All" onClick={() => setDivision(null)} />
                                {divisions.map((d) => (
                                    <DropdownItem
                                        key={d}
                                        label={divisionDisplayNames[d] || d}
                                        onClick={() => setDivision(d)}
                                    />
                                ))}
                            </ul>
                        </div>
                    </div>
                    </>)}

                    {/* Role (role mode) */}
                    {searchMode === "role" && (
                        <div className="col-6 col-md-2">
                            <span style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "13px" }}>Role</span>
                            <div className="dropdown">
                                <button
                                    className="btn dropdown-toggle w-100"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    style={dropdownBtnStyle}
                                >
                                    {roleDisplayNames[roleCategory] || roleCategory}
                                </button>
                                <ul className="dropdown-menu w-100" style={dropdownMenuStyle}>
                                    {availableRoles.map((r) => (
                                        <DropdownItem
                                            key={r}
                                            label={roleDisplayNames[r] || r}
                                            onClick={() => setRoleCategory(r)}
                                        />
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Map (both modes, optional for role, required for area) */}
                    <div className="col-6 col-md-2">
                        <span style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "13px" }}>Map</span>
                        <div className="dropdown">
                            <button
                                className="btn dropdown-toggle w-100"
                                type="button"
                                data-bs-toggle="dropdown"
                                style={dropdownBtnStyle}
                            >
                                {selectedMap || (searchMode === "area" ? "Select Map" : "All Maps")}
                            </button>
                            <ul className="dropdown-menu w-100" style={dropdownMenuStyle}>
                                {searchMode === "role" && (
                                    <DropdownItem label="All Maps" onClick={() => setSelectedMap(null)} />
                                )}
                                {maps.map((m) => (
                                    <DropdownItem key={m} label={m} onClick={() => setSelectedMap(m)} />
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Area (area mode only) */}
                    {searchMode === "area" && selectedMap && (
                        <div className="col-6 col-md-2">
                            <span style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "13px" }}>Area</span>
                            <div className="dropdown">
                                <button
                                    className="btn dropdown-toggle w-100"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    style={dropdownBtnStyle}
                                >
                                    {selectedAreaName || "Select Area"}
                                </button>
                                <ul className="dropdown-menu w-100" style={dropdownMenuStyle}>
                                    {areas.map((a) => (
                                        <DropdownItem
                                            key={a.id}
                                            label={a.areaName}
                                            onClick={() => setSelectedAreaId(a.id)}
                                        />
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>

                {/* Role threshold filters (hidden for AWP) */}
                {searchMode === "role" && roleCategory !== "awp" && (
                    <div className="row justify-content-center mb-3 g-3">
                        <div className="col-6 col-md-3">
                            <span style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "13px" }}>
                                Min. Rounds: {minRounds}
                            </span>
                            <input
                                type="range"
                                className="form-range"
                                min={0}
                                max={100}
                                step={5}
                                value={minRounds}
                                onChange={(e) => setMinRounds(parseInt(e.target.value))}
                                style={{ accentColor: "#e25623" }}
                            />
                        </div>
                        <div className="col-6 col-md-3">
                            <span style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "13px" }}>
                                Min. Role %: {minRolePercentage}%
                            </span>
                            <input
                                type="range"
                                className="form-range"
                                min={0}
                                max={100}
                                step={5}
                                value={minRolePercentage}
                                onChange={(e) => setMinRolePercentage(parseInt(e.target.value))}
                                style={{ accentColor: "#e25623" }}
                            />
                        </div>
                    </div>
                )}

                {/* Search Button */}
                <button
                    className="btn mb-4"
                    onClick={handleSearch}
                    disabled={loading || (dataSource === "league" && season === 0) || (searchMode === "area" && !selectedAreaId)}
                    style={{
                        backgroundColor: "#e25623",
                        color: "white",
                        fontWeight: "bold",
                        padding: "10px 32px",
                        borderRadius: "6px",
                        opacity: loading || season === 0 || (searchMode === "area" && !selectedAreaId) ? 0.5 : 1,
                    }}
                >
                    {loading ? "Searching..." : "Search"}
                </button>

                {/* Results */}
                {error && (
                    <div className="text-center mb-3">
                        <p style={{ color: "#ff6b6b" }}>{error}</p>
                    </div>
                )}

                {loading && <Loading />}

                {results && results.players.length === 0 && !loading && (
                    <div className="text-center mb-3">
                        <p style={{ color: "rgba(255,255,255,0.6)" }}>
                            No players found matching your criteria. Try lowering the minimum threshold or changing filters.
                        </p>
                    </div>
                )}

                {results && results.players.length > 0 && (
                    <div
                        style={{
                            background: "linear-gradient(135deg, #1a1a1a 0%, #252525 100%)",
                            borderRadius: "12px",
                            overflow: "hidden",
                            border: "1px solid rgba(255, 102, 0, 0.1)",
                            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                        }}
                    >
                        {/* Header */}
                        <div
                            style={{
                                background: "linear-gradient(135deg, #2a2a2a 0%, #333333 100%)",
                                padding: "12px 16px",
                                borderBottom: "2px solid rgba(255, 102, 0, 0.15)",
                            }}
                        >
                            <h5 style={{ margin: 0, color: "#e2e8f0", fontSize: "1.05rem" }}>
                                {searchMode === "role"
                                    ? `Best ${roleDisplayNames[roleCategory] || roleCategory}s`
                                    : `Best players at ${selectedAreaName || "selected area"}`}
                                {selectedMap && searchMode === "role" ? ` on ${selectedMap}` : ""}
                                {side ? ` (${side} side)` : ""}
                                {dataSource === "demos" ? " — my demos" : " — Season " + results.season}
                            </h5>
                        </div>

                        {/* Table */}
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr
                                        style={{
                                            background: "linear-gradient(135deg, #2a2a2a 0%, #333333 100%)",
                                        }}
                                    >
                                        {([
                                            { label: "#", key: null },
                                            { label: "Player", key: null },
                                            { label: "Team", key: null },
                                            { label: "Division", key: null },
                                            ...(searchMode === "role"
                                                ? [{ label: "Role Rounds", key: "roleRounds" }, { label: "Role %", key: "rolePercentage" }]
                                                : [{ label: "Time", key: "time" }, { label: "Area %", key: "areaPercentage" }]),
                                            { label: "Games", key: "games" },
                                            { label: "Rating", key: "rating" },
                                            { label: "ADR", key: "adr" },
                                            { label: "K/D", key: "kd" },
                                            { label: "KAST", key: "kast" },
                                        ] as { label: string; key: string | null }[]).map((col) => (
                                            <th
                                                key={col.label}
                                                onClick={col.key ? () => handleSort(col.key!) : undefined}
                                                style={{
                                                    color: sortColumn === col.key ? "#e25623" : "#e2e8f0",
                                                    padding: "8px 10px",
                                                    fontWeight: 600,
                                                    fontSize: "13px",
                                                    textAlign: ["Player", "Team", "Division"].includes(col.label) ? "left" : "center",
                                                    whiteSpace: "nowrap",
                                                    cursor: col.key ? "pointer" : "default",
                                                    userSelect: "none",
                                                }}
                                            >
                                                {col.label}
                                                {sortColumn === col.key && (sortDirection === "asc" ? " ▲" : " ▼")}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedPlayers.map((player, index) => {
                                        const rowBG =
                                            index % 2 === 0
                                                ? "rgba(42, 42, 42, 0.5)"
                                                : "rgba(36, 36, 36, 0.5)";

                                        return (
                                            <tr
                                                key={player.steamId}
                                                style={{
                                                    backgroundColor: rowBG,
                                                    color: "#e2e8f0",
                                                    transition:
                                                        "background-color 0.2s ease, box-shadow 0.2s ease",
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor =
                                                        "rgba(255, 102, 0, 0.05)";

                                                    e.currentTarget.style.boxShadow =
                                                        "0 2px 8px rgba(255, 102, 0, 0.1)";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = rowBG;

                                                    e.currentTarget.style.boxShadow = "none";
                                                }}
                                            >
                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                        padding: "8px",
                                                        fontWeight: "bold",
                                                        color: "#e25623",
                                                        width: "40px",
                                                    }}
                                                >
                                                    {index + 1}
                                                </td>
                                                <td style={{ padding: "8px 10px" }}>
                                                    <Link
                                                        to={`/player/${player.steamId}`}
                                                        className="text-decoration-none text-light"
                                                    >
                                                        {player.playerName || player.steamId}
                                                    </Link>
                                                </td>
                                                <td
                                                    style={{
                                                        padding: "8px 10px",
                                                        color: "rgba(255,255,255,0.7)",
                                                        fontSize: "13px",
                                                    }}
                                                >
                                                    {player.teamName || "—"}
                                                </td>
                                                <td
                                                    style={{
                                                        padding: "8px 10px",
                                                        color: "rgba(255,255,255,0.6)",
                                                        fontSize: "13px",
                                                    }}
                                                >
                                                    {player.division && player.division !== "UserUpload"
                                                        ? divisionDisplayNames[player.division] || player.division
                                                        : "—"}
                                                </td>

                                                {searchMode === "role" ? (
                                                    <>
                                                        <td
                                                            style={{
                                                                textAlign: "center",
                                                                padding: "8px",
                                                                fontSize: "13px",
                                                            }}
                                                        >
                                                            {player.roleRounds}
                                                        </td>
                                                        <td
                                                            style={{
                                                                textAlign: "center",
                                                                padding: "8px",
                                                                fontSize: "13px",
                                                                color: "rgba(255,255,255,0.6)",
                                                            }}
                                                        >
                                                            {(player.rolePercentage * 100).toFixed(0)}%
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td
                                                            style={{
                                                                textAlign: "center",
                                                                padding: "8px",
                                                                fontSize: "13px",
                                                            }}
                                                        >
                                                            {formatTime(player.totalTimeSeconds)}
                                                        </td>
                                                        <td
                                                            style={{
                                                                textAlign: "center",
                                                                padding: "8px",
                                                                fontSize: "13px",
                                                                color: "rgba(255,255,255,0.6)",
                                                            }}
                                                        >
                                                            {(player.areaTimePercentage * 100).toFixed(1)}%
                                                        </td>
                                                    </>
                                                )}

                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                        padding: "8px",
                                                        fontSize: "13px",
                                                    }}
                                                >
                                                    {player.gamesCount}
                                                </td>
                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                        padding: "8px",
                                                        fontWeight: "bold",
                                                        color: ratingColor(player.avgRating21),
                                                    }}
                                                >
                                                    {player.avgRating21.toFixed(2)}
                                                </td>
                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                        padding: "8px",
                                                        fontSize: "13px",
                                                    }}
                                                >
                                                    {player.avgAdr.toFixed(1)}
                                                </td>
                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                        padding: "8px",
                                                        fontSize: "13px",
                                                    }}
                                                >
                                                    {player.avgKd.toFixed(2)}
                                                </td>
                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                        padding: "8px",
                                                        fontSize: "13px",
                                                    }}
                                                >
                                                    {player.avgKast.toFixed(1)}%
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

function ratingColor(rating: number): string {
    if (rating >= 1.2) return "#4ade80";
    if (rating >= 1.0) return "#e2e8f0";
    if (rating >= 0.8) return "#fbbf24";
    return "#f87171";
}

function formatTime(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
}

export default PlayerScoutPage;
